import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { listarClientes, limpiarClientes, limpiarDetallesCliente } from '../../actions/clientes';

import AgregarClienteModal from './modals/AgregarClienteModal';
import EditarClienteModal from './modals/EditarClienteModal';
import EliminarClienteModal from './modals/EliminarClienteModal';

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const Clientes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { listado } = useSelector(state => state.clientes || { listado: [] });

    const [textoFiltro, setTextoFiltro] = useState('');
    const [modalNuevo, setModalNuevo] = useState(false);
    const [clienteEditar, setClienteEditar] = useState(null);
    const [clienteEliminar, setClienteEliminar] = useState(null);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: true, nombre: 'Clientes', path: '/pdv/clientes' }
        ]));
    }, [dispatch]);

    useEffect(() => {
        dispatch(listarClientes());

        return () => {
            dispatch(limpiarClientes());
            dispatch(limpiarDetallesCliente());
        };
    }, [dispatch]);

    const dataFiltrada = useMemo(() => {
        const q = (textoFiltro || '').trim().toLowerCase();
        const data = (listado || []).filter(c => !c?.deleted);

        if (!q) return data;

        return data.filter(c => {
            const nombre = (c?.nombre || '').toLowerCase();
            const correo = (c?.correo || '').toLowerCase();
            const telefono = (c?.telefono || '').toLowerCase();
            const rfc = (c?.rfc || '').toLowerCase();
            return (
                nombre.includes(q) ||
                correo.includes(q) ||
                telefono.includes(q) ||
                rfc.includes(q)
            );
        });
    }, [listado, textoFiltro]);

    const columns = useMemo(() => ([
        {
            name: 'Nombre',
            selector: row => row?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Correo',
            selector: row => row?.correo || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Teléfono',
            selector: row => row?.telefono || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'RFC',
            selector: row => row?.rfc || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Balance (MXN)',
            selector: row => Number(row?.saldo_pendiente || 0),
            sortable: true,
            right: true,
            cell: row => {
                const saldo = Number(row?.saldo_pendiente || 0);
                const tone = saldo > 0 ? 'warning' : 'success';
                return (
                    <div className={`fw-bold text-${tone}`} style={{ width: '100%', textAlign: 'right' }}>
                        {formatMoney(saldo)}
                    </div>
                );
            },
            grow: 1
        },
        {
            name: 'Opciones',
            width: '140px',
            cell: row => {
                return (
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Ver detalle"
                            onClick={() => navigate(`/clientes/detalle/${row._id}`)}
                        >
                            <i className="fas fa-eye text-primary" />
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Editar"
                            onClick={() => setClienteEditar(row)}
                        >
                            <i className="fa fa-edit text-warning" />
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Eliminar"
                            onClick={() => setClienteEliminar(row)}
                        >
                            <i className="fas fa-trash text-danger" />
                        </button>
                    </div>
                );
            }
        }
    ]), [navigate]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    const onChangeFiltroTabla = (e) => {
        setTextoFiltro(e.target.value);
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Clientes</h3>

                    <div className="d-flex justify-content-end mb-3">
                        <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                            <div style={{ minWidth: 280 }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar en la tabla..."
                                    value={textoFiltro}
                                    onChange={onChangeFiltroTabla}
                                />
                            </div>

                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    dispatch(limpiarDetallesCliente());
                                    setModalNuevo(true);
                                }}
                            >
                                <i className="feather icon-plus" /> Agregar cliente
                            </button>
                        </div>
                    </div>

                    <div className="mt-2">
                        <DataTable
                            columns={columns}
                            data={dataFiltrada || []}
                            pagination
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover
                            striped
                            responsive
                            persistTableHead
                        />
                    </div>
                </div>
            </div>

            <AgregarClienteModal
                show={modalNuevo}
                onHide={() => setModalNuevo(false)}
            />

            <EditarClienteModal
                show={!!clienteEditar}
                idCliente={clienteEditar?._id || null}
                onHide={() => {
                    setClienteEditar(null);
                    dispatch(limpiarDetallesCliente());
                }}
            />

            <EliminarClienteModal
                show={!!clienteEliminar}
                idCliente={clienteEliminar?._id || null}
                onHide={() => setClienteEliminar(null)}
            />
        </Fragment>
    );
};

export default Clientes;
